import React, { useContext, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import SubNavRail from '@/components/SubNavRail';
import AppContext from '@/contexts/appContext';
import useBetaFeature from '@/hooks/useBetaFeature';
import PersonContext from '@/pages/Person/PersonContext';
import { Avatar } from '@/ui/Avatar/Avatar';
import formatPersonName from '@/utils/formatPersonName';
import { getPersonNavOptions } from '@/utils/personNavOptions';
import resolveCurrentNavLabel from '@/utils/resolveCurrentNavLabel';

import type { Props } from './types';

/**
 * The second desktop rail, shown while a person is open. It sits immediately right of the
 * workspace rail and mirrors the options in the narrow-layout PersonNav `<select>`.
 *
 * A page that opens a record off to the side of those pages — an encounter — passes it as
 * `openRecord` so the rail lists it too, rather than leaving nothing marked current.
 */
const PersonSidebarNav = ({ openRecord }: Props) => {
  const { pathname } = useLocation();
  const { permissions } = useContext(AppContext);
  const { person } = useContext(PersonContext);
  const personRoot = `/people/${person.id}`;
  const { isEnabled: isInsurancePageHidden } = useBetaFeature('profileInsurance');

  const items = useMemo(
    () => [
      // Pinned, so sorting the person's pages A–Z never buries the thing you are looking at.
      ...(openRecord ? [{ ...openRecord, pinned: true }] : []),
      ...getPersonNavOptions(person.id, permissions!, { isInsurancePageHidden })
    ],
    [isInsurancePageHidden, openRecord, permissions, person.id]
  );

  const currentLabel = resolveCurrentNavLabel(items, pathname);

  const name = formatPersonName({ person, withPronouns: false });

  // `mp-sensitive` keeps the member's name out of Mixpanel autocapture text and attributes.
  return (
    <SubNavRail
      ariaLabel="Person"
      title={name}
      items={items}
      currentLabel={currentLabel}
      header={
        <Link
          to={`${personRoot}/profile`}
          aria-label={`${name} profile`}
          className="mp-sensitive flex min-w-0 items-center gap-x-2 text-gray-900"
          title={name}>
          <Avatar src={person.profile_image_url ?? undefined} name={name} size="sm" />
          <span className="truncate text-sm font-semibold">{name}</span>
        </Link>
      }
      collapsedHeader={
        <Link
          to={`${personRoot}/profile`}
          aria-label={`${name} profile`}
          className="mp-sensitive mx-auto flex items-center"
          title={name}>
          <Avatar src={person.profile_image_url ?? undefined} name={name} size="sm" />
        </Link>
      }
    />
  );
};

export default React.memo(PersonSidebarNav);
